/**
 * History wording for a recorded line-description edit (backend intent `billable_item_description`,
 * stored with the old and new text under `line_description`).
 */

export const DESCRIPTION_HISTORY_MAX_CHARS = 120;
/** How much unchanged text to keep in front of the first difference when a long line is windowed. */
const LEAD_CONTEXT_CHARS = 30;

function flatten(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

/** Collapse whitespace (descriptions can be multi-line) and cut long text at a word boundary with an ellipsis. */
export function truncateDescription(text: string, max = DESCRIPTION_HISTORY_MAX_CHARS): string {
  const flat = flatten(text);
  if (flat.length <= max) return flat;
  const cut = flat.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

/** `max` characters of `text` from `start`, with an ellipsis on each side that was cut. */
function windowOf(text: string, start: number, max: number): string {
  const end = start + max;
  return `${start > 0 ? '…' : ''}${text.slice(start, end)}${end < text.length ? '…' : ''}`;
}

/**
 * Old and new text as shown in the summary line. Short text is shown whole. Long text is shown as a window
 * that starts just before the first character that differs, so a small edit late in a long line is visible
 * on both sides instead of two identical truncated prefixes.
 */
export function descriptionChangeExcerpts(oldText: string, newText: string, max = DESCRIPTION_HISTORY_MAX_CHARS): [string, string] {
  const a = flatten(oldText);
  const b = flatten(newText);
  if (a.length <= max && b.length <= max) return [a, b];
  let first = 0;
  while (first < a.length && first < b.length && a[first] === b[first]) first += 1;
  if (first < max - LEAD_CONTEXT_CHARS) {
    const truncated: [string, string] = [truncateDescription(a, max), truncateDescription(b, max)];
    // Word-boundary truncation can cut back before the difference; never show two identical sides.
    if (truncated[0] !== truncated[1]) return truncated;
  }
  let start = Math.max(0, first - LEAD_CONTEXT_CHARS);
  const space = a.lastIndexOf(' ', start);
  if (space > 0 && start - space < LEAD_CONTEXT_CHARS) start = space + 1;
  return [windowOf(a, start, max), windowOf(b, start, max)];
}

function recorded(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  return value.trim() === '' ? null : value;
}

/** True when both sides were recorded and differ only in spacing or line breaks. */
export function isWhitespaceOnlyChange(before: unknown, after: unknown): boolean {
  return typeof before === 'string' && typeof after === 'string' && before !== after
    && flatten(before) === flatten(after) && flatten(before) !== '';
}

/** `Description changed from "old" to "new".`, with honest wording when either side was blank or not recorded. */
export function describeDescriptionChange(before: unknown, after: unknown): string {
  if (isWhitespaceOnlyChange(before, after)) {
    return 'Description changed: only spacing or line breaks changed. The wording is the same.';
  }
  const oldText = recorded(before);
  const newText = recorded(after);
  if (oldText !== null && newText !== null) {
    const [shownOld, shownNew] = descriptionChangeExcerpts(oldText, newText);
    return `Description changed from "${shownOld}" to "${shownNew}".`;
  }
  if (newText !== null) {
    return before === '' || (typeof before === 'string' && before.trim() === '')
      ? `Description changed from blank to "${truncateDescription(newText)}".`
      : `Description changed to "${truncateDescription(newText)}". The earlier wording was not recorded.`;
  }
  if (oldText !== null) {
    return typeof after === 'string'
      ? `Description changed from "${truncateDescription(oldText)}" to blank.`
      : `Description changed from "${truncateDescription(oldText)}". The new wording was not recorded.`;
  }
  return 'Description changed. The old and new wording were not recorded.';
}

/** Whether the summary line had to shorten either side, so a "Show full wording" control is worth offering. */
export function descriptionChangeIsShortened(before: unknown, after: unknown): boolean {
  const long = (value: unknown) => typeof value === 'string' && flatten(value).length > DESCRIPTION_HISTORY_MAX_CHARS;
  return long(before) || long(after) || isWhitespaceOnlyChange(before, after);
}
